import { DestroyRef, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PreferenceStorageService } from '@app/shared/services/preference-storage.service';
import { RecentlyUsedEntry, RecentlyUsedService } from '@app/shared/services/recently-used.service';
import { groupBy, sortBy } from '@app/utils/common';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { Command, SupportedOrder, TranslatedCommandResultEntry } from '.';

@Injectable({
  providedIn: 'root',
})
export class SortOrderCommandService {
  private commandOrder: SupportedOrder = 'default';
  private _sortOrder$: Observable<SupportedOrder>;

  private _commandUsage$: Observable<Record<string, RecentlyUsedEntry>>;

  get commandUsage$(): Observable<Record<string, RecentlyUsedEntry>> {
    return this._commandUsage$;
  }

  get sortOrder$(): Observable<SupportedOrder> {
    return this._sortOrder$.pipe(shareReplay(1));
  }

  constructor(
    private destroyRef: DestroyRef,
    private preferenceStorageService: PreferenceStorageService,
    private recentlyUsedService: RecentlyUsedService
  ) {}

  initialize(): void {
    this._commandUsage$ = this.recentlyUsedService.list$('quick-command').pipe(
      map((entries: RecentlyUsedEntry[]) =>
        groupBy(
          entries,
          (entry) => entry.id,
          (values) => values[0]
        )
      ),
      takeUntilDestroyed(this.destroyRef)
    );

    this._sortOrder$ = this.preferenceStorageService.preference$<SupportedOrder>(
      'quick-commands:command-order',
      'default'
    );

    this._sortOrder$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((order) => {
      this.commandOrder = order;
    });
  }

  sortCommands(commands: TranslatedCommandResultEntry[]): TranslatedCommandResultEntry[] {
    if (this.commandOrder !== 'default') {
      return this.recentlyUsedService.sort({
        compare: (result, id) => result.command?.name == id,
        equalSortOrder: [['label', 'asc']],
        items: commands,
        historyName: 'quick-command',
        order: this.commandOrder,
      });
    } else {
      return commands.sort(sortBy([['label', 'asc']]));
    }
  }

  remember(command: Command): void {
    if (command.countInvocations) {
      this.recentlyUsedService.add({
        historyName: 'quick-command',
        id: command.name,
      });
    }
  }
}
